import React from 'react';
import {
    DEFAULT_BILLING_CURRENCY,
    parseInvoiceBreakdown,
    buildInvoiceChargeLineRows,
    resolveInvoiceTaxSnapshot,
    bigAmountFormat,
    isCheckoutInvoice,
    formatMetricPrice,
} from '../../utils';

const sectionTitleStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 8,
};

const thStyle = {
    padding: '8px 10px',
    textAlign: 'right',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: 12,
};

/**
 * Invoice charge summary — same layout as PDF (Description / Qty / Unit Price / Amount + TOTALS).
 */
export default function BillView({
    breakdown,
    invoiceAmount,
    currencySymbol,
    planName,
    invoiceKind,
}) {
    const bd = parseInvoiceBreakdown(breakdown);
    const sym = currencySymbol || DEFAULT_BILLING_CURRENCY?.symbol;
    const isCheckout = isCheckoutInvoice({ invoice_kind: invoiceKind });
    const lineRows = buildInvoiceChargeLineRows({
        breakdown: bd,
        planName,
        invoiceAmount,
        isCheckout,
    });
    const tax = resolveInvoiceTaxSnapshot(bd, invoiceAmount);

    if (!lineRows.length) return null;

    return (
        <div style={{ marginBottom: 14 }}>
            <div style={sectionTitleStyle}>Charge summary</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
                <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={{ ...thStyle, textAlign: 'left' }}>Description</th>
                        <th style={thStyle}>Qty</th>
                        <th style={thStyle}>Unit Price ({sym})</th>
                        <th style={thStyle}>Amount ({sym})</th>
                    </tr>
                </thead>
                <tbody>
                    {lineRows.map((row) => (
                        <tr key={row.title} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '8px 10px', color: '#374151', fontWeight: 600 }}>{row.title}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Number(row.qty).toLocaleString()}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', color: '#6b7280' }}>
                                {formatMetricPrice(sym, row.rate)}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>
                                {bigAmountFormat(row.amount, sym, "decimal")}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ minWidth: 220, fontSize: 13 }}>
                    <div style={{ fontWeight: 700, textAlign: 'right', marginBottom: 8, color: '#374151' }}>TOTALS</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, padding: '4px 0', color: '#6b7280' }}>
                        <span>Subtotal:</span>
                        <span>{bigAmountFormat(tax.subtotal_before_tax, sym)}</span>
                    </div>
                    {(tax.gst_percent > 0 || tax.gst_amount > 0) && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, padding: '4px 0', color: '#6b7280' }}>
                            <span>GST ({tax.gst_percent}%):</span>
                            <span>{bigAmountFormat(tax.gst_amount, sym)}</span>
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, padding: '6px 0 0', fontWeight: 700, color: '#111827', borderTop: '1px solid #e5e7eb', marginTop: 4 }}>
                        <span>Total:</span>
                        <span style={{ color: '#4f46e5' }}>{bigAmountFormat(tax.grand_total, sym)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
